import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { useRouter } from 'vue-router'
import { computed } from 'vue'
import {
  getImpersonationStatus,
  IMPERSONATION_STATUS_QUERY_KEY,
  startImpersonation,
  stopImpersonation,
  type ImpersonationStatusResponse,
} from '@/features/admin/users/api/impersonation.api'
import authStore from '@/features/auth/stores/auth/auth.store'
import { PermissionsGroupsEnumType, RoleEnumTypeV2 } from '@/features/auth/types'
import useToast from '@/hooks/use-toasts.hook'

// Helper function to decode JWT without verification
function decodeJWT(token: string) {
  try {
    const base64Url = token.split('.')[1]
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    )
    return JSON.parse(jsonPayload)
  } catch (error) {
    console.error('Error decoding JWT:', error)
    return null
  }
}

export function useImpersonation() {
  const queryClient = useQueryClient()
  const router = useRouter()
  const toast = useToast()

  // Check if currently impersonating by decoding the JWT
  const isImpersonating = computed(() => {
    const token = authStore.authToken
    if (!token) return false

    const decoded = decodeJWT(token)
    return decoded?.impersonation?.isImpersonating === true
  })

  // Query to get current impersonation status (only if impersonating)
  const {
    data: impersonationStatus,
    isLoading: isLoadingStatus,
    refetch: refetchStatus,
  } = useQuery<ImpersonationStatusResponse>({
    queryKey: [IMPERSONATION_STATUS_QUERY_KEY],
    queryFn: getImpersonationStatus,
    retry: false,
    staleTime: 0, // Always refetch to ensure fresh data
    enabled: isImpersonating.value, // Only fetch if impersonating
  })

  // Mutation to start impersonation
  const { mutate: startImpersonationMutation, isPending: isStarting } = useMutation({
    mutationFn: ({ targetUserId, reason }: { targetUserId: string; reason: string; targetUserRoles: string[] }) =>
      startImpersonation(targetUserId, reason),
    onSuccess: async (data, variables) => {
      // Update the token in the auth store
      await authStore.setAuthToken(data.token)

      // Determine which interface to show based on target user roles
      const userRoles = variables.targetUserRoles || []
      const isAdminOrManagerOrSupport =
        userRoles.includes(RoleEnumTypeV2.ADMIN) ||
        userRoles.includes(RoleEnumTypeV2.MANAGER) ||
        userRoles.includes(RoleEnumTypeV2.SUPPORT)
      const isMentorOrMentee =
        userRoles.includes(RoleEnumTypeV2.MENTOR) || userRoles.includes(RoleEnumTypeV2.MENTEE)

      // Set the appropriate permission group
      if (isAdminOrManagerOrSupport) {
        authStore.setUserActivePermissionGroup(PermissionsGroupsEnumType.ADMIN)
      } else if (isMentorOrMentee) {
        authStore.setUserActivePermissionGroup(PermissionsGroupsEnumType.USER)
      }

      // Invalidate all queries to refetch with new token
      queryClient.invalidateQueries()

      // Show success toast
      toast.success(`Vous êtes maintenant connecté en tant que ${data.targetUser.email}`)

      // Full page reload to refresh all state and apply new permissions
      window.location.href = '/'
    },
    onError: (error: any) => {
      toast.error(error.message || "Impossible de démarrer l'impersonation")
    },
  })

  // Mutation to stop impersonation
  const { mutate: stopImpersonationMutation, isPending: isStopping } = useMutation({
    mutationFn: stopImpersonation,
    onSuccess: async (data) => {
      // Update the token in the auth store
      await authStore.setAuthToken(data.token)

      // When stopping impersonation, return to admin interface
      authStore.setUserActivePermissionGroup(PermissionsGroupsEnumType.ADMIN)

      // Invalidate all queries to refetch with new token
      queryClient.invalidateQueries()

      // Show success toast
      toast.success(`Vous êtes de retour sur votre compte (${data.user.email})`)

      // Full page reload to refresh all state and redirect to admin
      window.location.href = '/'
    },
    onError: (error: any) => {
      toast.error(error.message || "Impossible d'arrêter l'impersonation")
    },
  })

  return {
    // Status
    impersonationStatus,
    isImpersonating,
    isLoadingStatus,

    // Actions
    startImpersonation: startImpersonationMutation,
    stopImpersonation: stopImpersonationMutation,
    isStarting,
    isStopping,

    // Refetch
    refetchStatus,
  }
}
